import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { getAsset } from '../../../../../../../../../util/assets';
import PrintItemOption from '../../../PhotoPurchasingOptions/components/PrintItemOption';
import {
  getIndividualPrintCartItem,
  getPhotoPricingPrintProduct,
} from '../../../../../../../../../services/checkout/redux/helpers';
import PrintProductSizeSuccessModal from '../PrintProductsGroup/components/PrintProductSizeSuccessModal';
import { getPhotoPricingPrintBackgrounds } from '../../../../../../../../../util/photo-pricings';
import useIdentityJerseyColors from '../../../../hooks/useIdentityJerseyColors';
import PrintProductIndividual from '../PrintProductIndividual';

const propTypes = {
  className: PropTypes.string,
  readOnly: PropTypes.bool,
  photoPricing: PropTypes.shape({
    id: PropTypes.string.isRequired,
    requires_package_purchase: PropTypes.bool.isRequired,
    print_items: PropTypes.shape({
      records: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          price: PropTypes.shape({
            value: PropTypes.number.isRequired,
          }).isRequired,
          type: PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
          }).isRequired,
        }).isRequired,
      ).isRequired,
    }).isRequired,
  }).isRequired,
  photo: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  album: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  cart: PropTypes.array.isRequired,
  onPrintItemUpdate: PropTypes.func.isRequired,
  onPrintItemToggle: PropTypes.func.isRequired,
  onPrintProductToggle: PropTypes.func.isRequired,
  onPrintProductUpdate: PropTypes.func.isRequired,
  onCheckout: PropTypes.func.isRequired,
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string,
  }),
  identity: PropTypes.shape({
    id: PropTypes.string,
  }),
  disabled: PropTypes.bool,
  disabledElement: PropTypes.node,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
  }),
};

const IndividualPrintsOption = ({
  className,
  readOnly,
  photoPricing,
  photo,
  album,
  onPrintItemToggle,
  onPrintItemUpdate,
  onPrintProductToggle,
  onPrintProductUpdate,
  cart,
  onCheckout,
  albumMembershipGroup,
  identity,
  disabled,
  disabledElement,
  watermarkSettings,
  isAllPhotos,
}) => {
  const classes = classNames({
    [className]: !!className,
    'purchasing-option-item': true,
    'purchasing-option-item-individual-print-items': true,
  });

  const { jerseyColors } = useIdentityJerseyColors({
    identityId: identity?.id,
    albumFolderId:
      albumMembershipGroup?.album_folder?.id ??
      albumMembershipGroup?.album?.root_album_folder?.id,
  });

  const printItems = photoPricing?.print_items?.records ?? [];
  const photoPricingPrintProducts =
    photoPricing?.print_product_items?.records ?? [];
  const printBackgrounds = photo?.foreground?.original_url
    ? getPhotoPricingPrintBackgrounds(
        photoPricing,
        albumMembershipGroup,
        jerseyColors,
      )
    : [];
  const [configuredAccountCard, setConfiguredAccountCard] = useState(null);

  return (
    <Fragment>
      {printItems?.length > 0 && (
        <div className={classes}>
          <div className="purchasing-option-item-thumb">
            <img
              width={60}
              src={getAsset('/simple-prints-illustration.svg')}
              alt=""
            />
          </div>

          <div className="purchasing-option-item-content-wrap">
            <span className="purchasing-option-item-title">
              Standard Prints
            </span>

            <div className="purchasing-option-item-content mb-2">
              {readOnly ? (
                <p className="text-sm lh-normal text-dark text-bold">
                  Available sizes:
                </p>
              ) : (
                <p className="text-sm lh-normal text-dark text-bold">
                  Select one or more print sizes below to add them to your cart:
                </p>
              )}
            </div>

            <div className="purchasing-option-item-print-list">
              {printItems.map(printItem => (
                <PrintItemOption
                  key={printItem.id}
                  inline={readOnly}
                  readOnly={readOnly}
                  onToggle={() =>
                    onPrintItemToggle({
                      photo,
                      album,
                      printItem,
                      photoPricing,
                    })
                  }
                  onUpdate={onPrintItemUpdate}
                  photo={photo}
                  printItem={printItem}
                  cartItem={getIndividualPrintCartItem(cart, {
                    printItemId: printItem.id,
                    photoId: photo?.id,
                  })}
                  disabled={disabled}
                  disabledElement={disabledElement}
                  watermarkSettings={watermarkSettings}
                  isAllPhotos={isAllPhotos}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {photoPricingPrintProducts.map(photoPricingPrintProduct => (
        <PrintProductIndividual
          photoPricingPrintProductItem={photoPricingPrintProduct}
          cartItem={getPhotoPricingPrintProduct(cart, {
            photoPricingPrintProductId: photoPricingPrintProduct?.id,
            photoId: photo?.id,
          })}
          onCartItemToggle={({ accountCard, roesConfiguration }) => {
            onPrintProductToggle({
              accountCard,
              roesConfiguration,
              photoPricingPrintProduct,
              photo,
              identity,
            });

            setConfiguredAccountCard(accountCard);
          }}
          onCartItemUpdate={onPrintProductUpdate}
          readOnly={readOnly}
          photo={photo}
          albumMembershipGroup={albumMembershipGroup}
          identity={identity}
          disabled={disabled}
          disabledElement={disabledElement}
          printBackgrounds={printBackgrounds}
          watermarkSettings={watermarkSettings}
          isAllPhotos={isAllPhotos}
        />
      ))}

      {configuredAccountCard && (
        <PrintProductSizeSuccessModal
          forceOpen
          accountCard={configuredAccountCard}
          onCheckout={() => {
            setConfiguredAccountCard(null);
            onCheckout();
          }}
          onDismiss={() => {
            setConfiguredAccountCard(null);
          }}
        />
      )}
    </Fragment>
  );
};

IndividualPrintsOption.propTypes = propTypes;

export default IndividualPrintsOption;
